import { Fragment, useEffect, useRef, useState } from "react";
import { ActionIcon, Badge, Box, Card, Group, Loader, ScrollArea, Stack, Text, Textarea } from "@mantine/core";
import { IconSend, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { fmtNumber } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { ErrorNotice, RequireFeature } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";

interface Turn {
  role: "user" | "ai";
  text: string;
  requestId?: string;
  tokens?: number;
}

const STARTERS = ["What's a high-protein vegetarian lunch under 500 kcal?", "Is oat milk or almond milk better for blood sugar?", "Give me a quick recipe using chickpeas and spinach", "How much fiber should I eat per day?"];

/** Minimal markdown: **bold**, bullet lines, and paragraph breaks. */
function RichText({ text }: { text: string }) {
  return (
    <>
      {text.split("\n").map((line, i) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) => (p.startsWith("**") && p.endsWith("**") ? <b key={j}>{p.slice(2, -2)}</b> : <Fragment key={j}>{p}</Fragment>));
        return <div key={i}>{line.trim() ? parts : <br />}</div>;
      })}
    </>
  );
}

export function AdvisorPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const viewport = useRef<HTMLDivElement>(null);
  const ask = useMutation({
    mutationFn: ({ question, history }: { question: string; history: Turn[] }) => {
      const context = history
        .slice(-6)
        .map((t) => `${t.role === "user" ? "User" : "Advisor"}: ${t.text}`)
        .join("\n");
      return passio.tools.nutritionAdvisor(question, context ? `Conversation so far:\n${context}` : undefined);
    },
    onSuccess: (res) => setTurns((t) => [...t, { role: "ai", text: res.data?.answer ?? "(No answer in the response. Check the inspector.)", requestId: res.meta.requestId, tokens: res.meta.requestUsage }]),
  });

  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: "smooth" });
  }, [turns, ask.isPending]);

  const send = (q = input) => {
    const question = q.trim();
    if (!question || ask.isPending) return;
    const history = turns;
    setTurns((t) => [...t, { role: "user", text: question }]);
    setInput("");
    ask.mutate({ question, history });
  };

  return (
    <RequireFeature feature="advisor">
      <PageHeader
        eyebrow="POST /napi/tools/nutritionAdvisor"
        title="Nutrition advisor"
        description="Ask about foods, recipes and nutrition. Regional context comes from the Passio-Product-Localization header, and replies are translated when Localization-ISO is set."
        actions={
          <Group gap="xs">
            <RouteModelPicker routes={["nutritionAdvisor"]} />
            {turns.length > 0 && (
              <ActionIcon variant="default" size="lg" onClick={() => setTurns([])} aria-label="Clear conversation">
                <IconTrash size={18} />
              </ActionIcon>
            )}
          </Group>
        }
      />
      <Card p={0} style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 290px)", minHeight: 420 }}>
        <ScrollArea style={{ flex: 1 }} viewportRef={viewport} p="lg">
          {turns.length === 0 ? (
            <Stack align="center" gap="sm" py="xl">
              <Text c="dimmed">Start with a question, or try one of these:</Text>
              <Group gap="xs" justify="center" maw={640}>
                {STARTERS.map((s) => (
                  <Badge key={s} size="lg" variant="outline" color="gray" tt="none" fw={500} style={{ cursor: "pointer" }} onClick={() => send(s)}>
                    {s}
                  </Badge>
                ))}
              </Group>
            </Stack>
          ) : (
            <Stack gap="md">
              {turns.map((t, i) => (
                <Stack key={i} gap={4} align={t.role === "user" ? "flex-end" : "flex-start"}>
                  <Box className={`pd-chat-bubble ${t.role}`}>{t.role === "ai" ? <RichText text={t.text} /> : t.text}</Box>
                  {t.role === "ai" && (
                    <Group gap="xs">
                      {t.tokens !== undefined && (
                        <Text fz={11} c="dimmed" ff="monospace">
                          {fmtNumber(t.tokens)} tokens
                        </Text>
                      )}
                      <FeedbackButtons requestId={t.requestId} compact />
                    </Group>
                  )}
                </Stack>
              ))}
              {ask.isPending && (
                <Group gap="xs">
                  <Loader size="xs" color="violet" type="dots" />
                  <Text fz="sm" c="dimmed">
                    Thinking…
                  </Text>
                </Group>
              )}
              {ask.error && <ErrorNotice error={ask.error} />}
            </Stack>
          )}
        </ScrollArea>
        <Box p="md" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
          <Group gap="sm" wrap="nowrap" align="flex-end">
            <Textarea
              style={{ flex: 1 }}
              autosize
              minRows={1}
              maxRows={5}
              placeholder="Ask the advisor…"
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <ActionIcon size={40} radius="md" onClick={() => send()} loading={ask.isPending} disabled={!input.trim()} aria-label="Send">
              <IconSend size={18} />
            </ActionIcon>
          </Group>
        </Box>
      </Card>
    </RequireFeature>
  );
}
